import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  SingleFamilySolidWasteProgram,
  SolidWasteEnumKey,
  SolidWasteEnumOption,
} from '@trp/nationaldb/interfaces';
import { CommunityWidgetComponent } from '../community-widget';
import { CommunityWidgetContentComponent } from '../community-widget-content';
import { CommunityWidgetHeaderComponent } from '../community-widget-header';

/** Options for every Single Family Solid Waste enumeration, keyed by enum. */
export type SolidWasteEnumOptionsMap = Record<
  SolidWasteEnumKey,
  SolidWasteEnumOption[]
>;

interface EnumFieldDefinition {
  enumKey: SolidWasteEnumKey;
  titleKey: string;
  icon: string;
  idField: keyof SingleFamilySolidWasteProgram;
  otherField: keyof SingleFamilySolidWasteProgram;
  multi: boolean;
}

interface EnumTile {
  titleKey: string;
  icon: string;
  available: boolean;
  value: string;
}

const ENUM_FIELDS: EnumFieldDefinition[] = [
  {
    enumKey: 'collectionType',
    titleKey: 'widgets.singleFamilySolidWasteProgram.typeOfCollection',
    icon: 'custom-solid:CollectionFormat',
    idField: 'collectionTypeId',
    otherField: 'collectionTypeOther',
    multi: false,
  },
  {
    enumKey: 'collectionWho',
    titleKey: 'widgets.singleFamilySolidWasteProgram.collectionWho',
    icon: 'global:SingleFamilyCollectionFrequency',
    idField: 'collectionWhoId',
    otherField: 'collectionWhoOther',
    multi: false,
  },
  {
    enumKey: 'frequencyOfCollection',
    titleKey: 'widgets.singleFamilySolidWasteProgram.collectionFrequency',
    icon: 'global:SingleFamilyCollectionFrequency',
    idField: 'frequencyOfCollectionId',
    otherField: 'frequencyOfCollectionOther',
    multi: false,
  },
  {
    enumKey: 'containerType',
    titleKey: 'widgets.singleFamilySolidWasteProgram.containerType',
    icon: 'custom-solid:Container',
    idField: 'containerTypeId',
    otherField: 'containerTypeOther',
    multi: false,
  },
  {
    enumKey: 'containerProvider',
    titleKey: 'widgets.singleFamilySolidWasteProgram.containerProvider',
    icon: 'custom-solid:Container',
    idField: 'containerProviderId',
    otherField: 'containerProviderOther',
    multi: false,
  },
  {
    enumKey: 'fundingSource',
    titleKey: 'widgets.singleFamilySolidWasteProgram.fundingSource',
    icon: 'custom-solid:MedianIncom',
    idField: 'fundingSourceIds',
    otherField: 'fundingSourceOther',
    multi: true,
  },
  {
    enumKey: 'wasteGenerationDisincentive',
    titleKey:
      'widgets.singleFamilySolidWasteProgram.wasteGenerationDisincentive',
    icon: 'global:table-chart',
    idField: 'wasteGenerationDisincentiveIds',
    otherField: 'wasteGenerationDisincentiveOther',
    multi: true,
  },
];

/**
 * Presentational widget tiles for the Single Family Solid Waste enumeration
 * fields (DATA-1667). Dumb component (type:ui): it receives the program and
 * the loaded enum options and resolves ids to display values. "Other" text is
 * appended when the selected option is the enumeration's `isOther` option.
 */
@Component({
  selector: 'trp-solid-waste-enum-widgets',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CommunityWidgetComponent,
    CommunityWidgetHeaderComponent,
    CommunityWidgetContentComponent,
  ],
  templateUrl: './solid-waste-enum-widgets.component.html',
  // display:contents removes this wrapper from layout so each emitted
  // <trp-community-widget> becomes a direct grid item of the parent
  // community-widget-row grid, instead of stacking inside a single cell.
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolidWasteEnumWidgetsComponent implements OnChanges {
  @Input() program!: SingleFamilySolidWasteProgram;
  @Input() enumOptions: SolidWasteEnumOptionsMap | null = null;

  tiles: EnumTile[] = [];

  ngOnChanges(): void {
    this.tiles = this.enumOptions ? this.buildTiles(this.enumOptions) : [];
  }

  private buildTiles(options: SolidWasteEnumOptionsMap): EnumTile[] {
    return ENUM_FIELDS.map((field) => {
      const value = field.multi
        ? this.resolveMulti(field, options[field.enumKey])
        : this.resolveSingle(field, options[field.enumKey]);
      return {
        titleKey: field.titleKey,
        icon: field.icon,
        available: !!value,
        value,
      };
    });
  }

  private resolveSingle(
    field: EnumFieldDefinition,
    options: SolidWasteEnumOption[]
  ): string {
    const id = this.program[field.idField] as number | null | undefined;
    const option = options.find((o) => o.id === id);
    if (!option) {
      return '';
    }
    return option.isOther
      ? this.withOther(option.value, field)
      : option.value;
  }

  private resolveMulti(
    field: EnumFieldDefinition,
    options: SolidWasteEnumOption[]
  ): string {
    const ids = (this.program[field.idField] as number[] | null) ?? [];
    if (ids.length === 0) {
      return '';
    }
    const selected = new Set(ids);
    const values = options
      .filter((o) => selected.has(o.id))
      .map((o) => (o.isOther ? this.withOther(o.value, field) : o.value));
    return values.join(', ');
  }

  private withOther(value: string, field: EnumFieldDefinition): string {
    const other = this.program[field.otherField] as string | null | undefined;
    return other ? `${value}: ${other}` : value;
  }
}
